<template>
  <div class="fee-rule-detail" v-if="!loading">
    <div class="back-btn" @click="goBack">← 返回列表</div>

    <div class="detail-header">
      <h2>规则详情</h2>
      <span :class="['status-badge', rule.status === 1 ? 'status-enabled' : 'status-disabled']">
        {{ rule.status === 1 ? '启用' : '禁用' }}
      </span>
    </div>

    <div class="detail-section">
      <h3>基本信息</h3>
      <div class="info-grid">
        <div class="info-item">
          <label>规则ID</label>
          <span>{{ rule.id }}</span>
        </div>
        <div class="info-item">
          <label>规则名称</label>
          <span>{{ rule.rule_name }}</span>
        </div>
        <div class="info-item">
          <label>收费类型</label>
          <span>{{ getFeeTypeText(rule.fee_type) }}</span>
        </div>
        <div class="info-item">
          <label>服务类型</label>
          <span>{{ getServiceTypeText(rule.service_type) }}</span>
        </div>
        <div class="info-item">
          <label>计算方式</label>
          <span>{{ getCalculationTypeText(rule.calculation_type) }}</span>
        </div>
        <div class="info-item">
          <label>起步价(元)</label>
          <span>{{ (rule.start_fee / 100).toFixed(2) }}</span>
        </div>
        <div class="info-item">
          <label>楼层费(元)</label>
          <span>{{ rule.floor_fee != null ? (rule.floor_fee / 100).toFixed(2) : '—' }}</span>
        </div>
        <div class="info-item">
          <label>优先级</label>
          <span>{{ rule.priority }}</span>
        </div>
        <div class="info-item">
          <label>排序</label>
          <span>{{ rule.sort_order }}</span>
        </div>
        <div class="info-item">
          <label>适用商家 ID</label>
          <span>{{ rule.shop_id || '全部商家' }}</span>
        </div>
        <div class="info-item">
          <label>适用城市 ID</label>
          <span>{{ rule.city_id || '全部城市' }}</span>
        </div>
        <div class="info-item">
          <label>适用小区 ID</label>
          <span>{{ rule.community_id || '全部小区' }}</span>
        </div>
        <div class="info-item">
          <label>有效期</label>
          <span>{{ formatDateRange(rule.effective_time, rule.expire_time) }}</span>
        </div>
      </div>
    </div>

    <div class="detail-section" v-if="rule.description">
      <h3>描述</h3>
      <p class="description-text">{{ rule.description }}</p>
    </div>

    <!-- 距离计费 -->
    <div class="detail-section" v-if="rule.calculation_type === 3 && parsedDistanceRules.length">
      <h3>距离计费规则</h3>
      <table class="sub-table">
        <thead>
          <tr>
            <th>距离下限(米)</th>
            <th>距离上限(米)</th>
            <th>基础费用(元)</th>
            <th>每100米加价(元)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(d, index) in parsedDistanceRules" :key="index">
            <td>{{ d.distance_min }}</td>
            <td>{{ d.distance_max === 0 ? '无上限' : d.distance_max }}</td>
            <td>{{ (d.base_fee / 100).toFixed(2) }}</td>
            <td>{{ (d.per_100m / 100).toFixed(2) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 特殊时段费 -->
    <div class="detail-section" v-if="rule.fee_type === 5 && parsedTimeRules.length">
      <h3>时段规则</h3>
      <table class="sub-table">
        <thead>
          <tr>
            <th>开始时间</th>
            <th>结束时间</th>
            <th>费用(元)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(t, index) in parsedTimeRules" :key="index">
            <td>{{ t.start_time }}</td>
            <td>{{ t.end_time }}</td>
            <td>{{ (t.fee / 100).toFixed(2) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 特殊天气费 -->
    <div class="detail-section" v-if="rule.fee_type === 6 && rule.calculation_type === 5 && parsedWeatherRules.length">
      <h3>天气加价规则</h3>
      <table class="sub-table">
        <thead>
          <tr>
            <th>天气类型</th>
            <th>加价(元)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(w, index) in parsedWeatherRules" :key="index">
            <td>{{ w.weather_type_code }}</td>
            <td>{{ (w.fee / 100).toFixed(2) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div class="loading-state" v-else>加载中...</div>
</template>

<script>
import request from '@/api/request'

export default {
  name: 'FeeRuleDetail',
  data() {
    return {
      rule: {},
      loading: true
    }
  },
  computed: {
    parsedDistanceRules() {
      return this.parseJsonField(this.rule.distance_rules)
    },
    parsedTimeRules() {
      return this.parseJsonField(this.rule.time_rules)
    },
    parsedWeatherRules() {
      return this.parseJsonField(this.rule.weather_rules)
    }
  },
  mounted() {
    this.loadDetail()
  },
  methods: {
    goBack() {
      this.$router.push('/orders/fee-rules')
    },
    async loadDetail() {
      try {
        const id = this.$route.params.id
        const result = await request({
          url: `/admin/fee-rules/${id}`,
          method: 'get'
        })
        if (result.code === 0) {
          this.rule = result.data
        } else {
        }
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    getFeeTypeText(type) {
      const map = { 1: '基础服务费', 2: '距离费', 3: '搬运费', 4: '楼层费', 5: '特殊时段费', 6: '特殊天气费' }
      return map[type] || '未知'
    },
    getServiceTypeText(type) {
      const map = { 0: '全部', 1: '代送物品', 2: '代买物品', 3: '代取快递', 4: '其他' }
      return map[type] || '未知'
    },
    getCalculationTypeText(type) {
      const map = { 1: '固定金额', 3: '按距离计费', 5: '天气规则' }
      return map[type] || '未知'
    },
    formatDistanceRange(min, max) {
      const minText = min === 0 ? '不限' : min
      const maxText = max === 0 ? '不限' : max
      return `${minText} - ${maxText}`
    },
    formatAmountRange(min, max) {
      const minText = min === 0 ? '不限' : (min / 100).toFixed(2)
      const maxText = max === 0 ? '不限' : (max / 100).toFixed(2)
      return `${minText} - ${maxText}`
    },
    formatDateRange(start, end) {
      if (!start) return '永久生效'
      const startDate = new Date(start).toLocaleDateString('zh-CN')
      const endDate = end ? new Date(end).toLocaleDateString('zh-CN') : '永久'
      return `${startDate} ~ ${endDate}`
    },
    // 兼容数据库二次 JSON 编码（字段里存的是字符串形式的数组）的情况。
    parseJsonField(val) {
      if (Array.isArray(val)) return val
      if (typeof val === 'string' && val.trim()) {
        try {
          let parsed = JSON.parse(val)
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed)
          }
          return Array.isArray(parsed) ? parsed : []
        } catch (e) {
          return []
        }
      }
      return []
    }
  }
}
</script>

<style scoped>
.fee-rule-detail {
  padding: 20px;
}

.back-btn {
  color: #3498db;
  cursor: pointer;
  margin-bottom: 20px;
  font-size: 14px;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
}

.detail-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-enabled {
  background: #d4edda;
  color: #155724;
}

.status-disabled {
  background: #f8d7da;
  color: #721c24;
}

.detail-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.detail-section h3 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item label {
  font-size: 13px;
  color: #999;
  margin-bottom: 5px;
}

.info-item span {
  font-size: 14px;
  color: #333;
}

.description-text {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.sub-table {
  width: 100%;
  border-collapse: collapse;
}

.sub-table th,
.sub-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.sub-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.loading-state {
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>
